import React, { FC, useContext, useState, useEffect } from 'react';
import classNames from 'classnames';
import { withSize, SizeMeProps } from 'react-sizeme';
import DefaultLink from 'next/link';

import Discount from 'components/discount';
import DiscountTermsModal from 'components/discountTermsModal';
import { Discount as IDiscount } from 'hooks/usePropertyDiscount';
import { i18n as I18n } from '../../../utils/i18n';
import { filteredKeyFacets } from '../../../utils/filters';

import { TABLET_WIDTH } from '../../../constants/breakpoints';

import { AuthContext } from '../../../state/auth';

import useFavourite from '../../../hooks/useFavourite';

import FeatureIcon from '../../shared/featureIcon';
import { FavouriteButton } from '../../shared/favouriteButton';
import SpacePrice from '../../spacePrice';
import AddToBookingButton from '../../addToBookingButton';
import FloorplansButton from '../../floorplansButton';

interface LinkProps {
  href: string;
}

const Link: FC<LinkProps> = ({ children, ...props }) => {
  return (
    <DefaultLink {...props}>
      <a>{children}</a>
    </DefaultLink>
  );
};

interface Venue {
  id: number;
  name: string;
}

interface Props {
  addedToBooking: boolean;
  disabled: boolean;
  id: number;
  image: string;
  name: string;
  venue: Venue;
  clinical: boolean;
  price: number;
  capacity: number;
  floor: string;
  floorplan: string;
  handleAddToBookingClick: () => null;
  size: SizeMeProps;
  facets: Array<Object>;
  linkComponent?: typeof Link;
  discount: IDiscount;
  discounts?: Array<Object>;
  categoryIds?: Array<number>;
}

const SpaceListItem: FC<Props> = ({
  addedToBooking = false,
  disabled = false,
  id,
  image,
  name,
  venue,
  clinical = false,
  price,
  capacity = null,
  floor = null,
  floorplan = null,
  handleAddToBookingClick,
  size,
  facets = [],
  discount = undefined,
  discounts = [],
  categoryIds,
  linkComponent: LinkComponent = Link,
}) => {
  const { loggedIn } = useContext(AuthContext);
  const [favourited, toggleFavourite] = useFavourite('spaces', id);
  const listItemTypeClasses = classNames({
    'space-list-item': true,
    'space-list-item--clinical': clinical,
    'space-list-item--non-clinical': !clinical,
  });

  const clinicalClasses = classNames({
    'clinical-indicator': true,
    'clinical-indicator--clinical': clinical,
    'clinical-indicator--non-clinical': !clinical,
  });

  const priceClasses = classNames({
    'space-list-item__price': true,
    'space-list-item__price--loggedin': loggedIn,
  });

  const [discountTermsModalIsOpen, setDiscountTermsModalIsOpen] = useState(
    false
  );

  const displayDiscount = () => {
    if (categoryIds.length === 0) {
      return false;
    }

    if (discount.roomTypeIds.length) {
      return discount.roomTypeIds.includes(categoryIds[0]);
    }

    return discount;
  };

  useEffect(() => {
    const anchor = window.location.hash.slice(1);
    const anchorEl = document.getElementById(anchor);

    if (anchor && anchorEl) {
      anchorEl.scrollIntoView(false);
    }
  }, []);

  return (
    <li key={id} className={listItemTypeClasses} data-cy="room">
      <p className="space-list-item__image-container">
        {image && (
          <img
            src={image}
            alt={`${I18n.t('space_list_item.photograph_alt')} ${name}, ${
              venue.name
            }`}
          />
        )}
        {!image && <span>{I18n.t('shared.picture_coming_soon')}</span>}

        {discount && displayDiscount() && (
          <>
            <Discount
              text={I18n.t('discounts.save', {
                percent: discount.amount,
              })}
              rotated
              size="small"
              onClick={() => setDiscountTermsModalIsOpen(true)}
            />

            <DiscountTermsModal
              isOpen={discountTermsModalIsOpen}
              onClose={() => setDiscountTermsModalIsOpen(false)}
              propertyOrRoomName={name}
              discounts={discounts}
            />
          </>
        )}
      </p>

      <div className="space-list-item__details-container">
        <div className="space-list-item__details">
          <h4 className={clinicalClasses}>
            {clinical
              ? I18n.t('shared.clinical')
              : I18n.t('shared.non_clinical')}
          </h4>
          <h3 className="space-list-item__details__name">
            <LinkComponent href={`${I18n.t('navigation.spaces.url')}/${id}`}>
              {name}
            </LinkComponent>
          </h3>
          <h5 className="space-list-item__details__venue-name">
            <LinkComponent
              href={`${I18n.t('navigation.venues.url')}/${venue.id}`}
            >
              {venue.name}
            </LinkComponent>
          </h5>
          <ul className="space-list-item__details__feature-list">
            <FloorplansButton
              venue={venue}
              space={{ id, floor, floorplan }}
              wrapper="li"
            />
            {filteredKeyFacets(facets).map(facet => (
              <li key={facet.name}>
                <FeatureIcon
                  name={facet.name}
                  icon={facet.icon}
                  iconAltText={facet.iconAltText}
                  displaySvgIcon={false}
                />
              </li>
            ))}
            {!clinical && capacity && (
              <li className="space-list-item__details__feature-list__feature">
                <FeatureIcon
                  type="room-capacity"
                  name={I18n.t('feature_icon.room_capacity', {
                    num: capacity,
                  })}
                  additionalText={String(capacity)}
                  displaySvgIcon
                />
              </li>
            )}
          </ul>
        </div>

        <div className="space-list-item__booking">
          <SpacePrice price={price} className={priceClasses} />
          <AddToBookingButton
            className="space-list-item__book-btn"
            onClick={handleAddToBookingClick}
            added={addedToBooking}
            enabled={!disabled}
            desktop={size.width >= TABLET_WIDTH}
          />
        </div>

        {loggedIn && (
          <FavouriteButton
            favouritedState={favourited}
            type="spaces"
            handleFavouriteButtonClick={toggleFavourite}
          />
        )}
      </div>
    </li>
  );
};

export default withSize()(SpaceListItem);
